import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

interface NavigationBlocker {
  id: string;
  priority: number;
  shouldBlock: () => boolean;
  onBlock: (location: Location) => {
    showModal: boolean;
    pendingNavigation?: () => void;
  };
}

interface UseCoordinatedNavigationBlockerReturn {
  registerBlocker: (blocker: NavigationBlocker) => void;
  unregisterBlocker: (id: string) => void;
  allowNavigation: () => void;
}

/**
 * Coordinated Navigation Blocker Hook
 *
 * Manages multiple navigation blockers in priority order to avoid React Router's
 * "A history supports only one prompt at a time" warning.
 *
 * Priority order:
 * 1. Slide Edit Reminder (priority: 1) - Higher priority, user might lose work
 * 2. Lesson Progress Tracking (priority: 2) - Lower priority
 */
const useCoordinatedNavigationBlocker = (): UseCoordinatedNavigationBlockerReturn => {
  const history = useHistory();
  const blockersRef = useRef<Map<string, NavigationBlocker>>(new Map());
  const unblockRef = useRef<(() => void) | null>(null);
  const pendingLocationRef = useRef<Location | null>(null);

  // Initialize the single history blocker
  useEffect(() => {
    unblockRef.current = history.block((location) => {
      pendingLocationRef.current = location;

      // Get all registered blockers sorted by priority (lower number = higher priority)
      const blockers = Array.from(blockersRef.current.values()).sort(
        (a, b) => a.priority - b.priority,
      );

      // Check each blocker in priority order
      const blockingBlocker = blockers.find((blocker) => blocker.shouldBlock());
      if (blockingBlocker) {
        const result = blockingBlocker.onBlock(location);
        if (result.showModal) {
          // Store the pending navigation for later use
          if (result.pendingNavigation) {
            result.pendingNavigation();
          }
          return false; // Block navigation
        }
      }

      // No blockers want to block, allow navigation
      return true;
    });

    // Cleanup on unmount
    return () => {
      if (unblockRef.current) {
        unblockRef.current();
      }
    };
  }, [history]);

  const registerBlocker = (blocker: NavigationBlocker) => {
    blockersRef.current.set(blocker.id, blocker);
  };

  const unregisterBlocker = (id: string) => {
    blockersRef.current.delete(id);
  };

  const allowNavigation = () => {
    if (unblockRef.current && pendingLocationRef.current) {
      unblockRef.current();
      history.push(
        pendingLocationRef.current.pathname + pendingLocationRef.current.search,
      );
      pendingLocationRef.current = null;
    }
  };

  return {
    registerBlocker,
    unregisterBlocker,
    allowNavigation,
  };
};

export default useCoordinatedNavigationBlocker;
export type { NavigationBlocker, UseCoordinatedNavigationBlockerReturn };
